import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Logo } from '../components/Logo';
import { Footer } from '../components/Footer';
import { auth, ApiError } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Banner } from './Login';

/**
 * Landing page for the "Choose a new password" link in the reset email
 * (/reset-password?token=…). The token is single-use and lives one hour.
 *
 * Google-only accounts can NOT reach this page: the backend issues no token for
 * them and refuses redemption, so an inbox alone can never mint a password on a
 * Google-linked account. They're told to use the Google button instead.
 *
 * A successful reset also signs them in (following a link sent to that inbox is
 * proof they control the address) and, server-side, revokes every other session
 * and extension token on the account.
 */
export function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  // Read the token ONCE into state, then strip it from the address bar. While
  // it sits in the URL it leaks three ways: into the browser history, into the
  // Referer header of any outbound link on this page (the logo points at
  // riffloop.app), and into anything the user copies or shares. It stays valid
  // for an hour, so none of those are free. Capturing it in a lazy initialiser
  // means the value survives the rewrite below.
  const [token] = useState(() => params.get('token') || '');
  useEffect(() => {
    if (params.get('token')) {
      window.history.replaceState({}, '', '/reset-password');
    }
    // Once, on mount — `params` is intentionally not a dependency.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    // Checked here rather than server-side: the confirm box exists to catch a
    // typo, and sending a mismatch would burn the single-use token for nothing.
    if (password !== confirm) {
      setErr("Those two passwords don't match.");
      return;
    }
    setErr(null);
    setBusy(true);
    try {
      await auth.resetPassword(token, password);
      await refresh();
      setDone(true);
      setTimeout(() => navigate('/', { replace: true }), 1200);
    } catch (e2) {
      setErr((e2 as ApiError)?.detail || 'Could not set the password. Please try again.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      style={{
        minHeight: 'calc(100vh - 60px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        gap: 24,
      }}
    >
      <div
        className="rl-card"
        style={{ maxWidth: 380, width: '100%', padding: 32, textAlign: 'center' }}
      >
        <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 16 }}>
          <Logo size="lg" variant="horizontal" linkTo="https://riffloop.app/" />
        </div>

        {!token ? (
          <>
            <h1 style={{ fontSize: 17, margin: '4px 0 8px' }}>This link is incomplete</h1>
            <p style={{ color: 'var(--text-dim)', fontSize: 13, lineHeight: 1.55 }}>
              Open the link straight from the email, or request a new one.
            </p>
            <a
              className="rl-btn-primary"
              href="/login"
              style={{
                display: 'block',
                width: '100%',
                padding: '12px 16px',
                fontSize: 14,
                marginTop: 16,
                textDecoration: 'none',
              }}
            >
              Back to log in
            </a>
          </>
        ) : done ? (
          <>
            <h1 style={{ fontSize: 17, margin: '4px 0 8px' }}>Password set</h1>
            <Banner tone="ok">You're signed in — taking you to the studio…</Banner>
          </>
        ) : (
          <>
            <h1 style={{ fontSize: 17, margin: '4px 0 8px' }}>Choose a new password</h1>
            <p
              style={{
                color: 'var(--text-dim)',
                fontSize: 13,
                margin: '0 0 20px',
                lineHeight: 1.55,
              }}
            >
              You'll be signed in straight after. Anywhere else that's signed into this account
              will be signed out.
            </p>
            <form onSubmit={onSubmit} style={{ display: 'grid', gap: 10, textAlign: 'left' }}>
              <div style={{ position: 'relative' }}>
                <input
                  className="rl-input"
                  type={showPw ? 'text' : 'password'}
                  autoComplete="new-password"
                  placeholder="New password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  maxLength={72}
                  disabled={busy}
                  autoFocus
                  style={{ paddingRight: 58 }}
                />
                <button
                  type="button"
                  onClick={() => setShowPw((v) => !v)}
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                  style={{
                    position: 'absolute',
                    right: 8,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-dim)',
                    fontSize: 12,
                    cursor: 'pointer',
                    padding: 4,
                  }}
                >
                  {showPw ? 'Hide' : 'Show'}
                </button>
              </div>
              <input
                className="rl-input"
                type={showPw ? 'text' : 'password'}
                autoComplete="new-password"
                placeholder="Confirm new password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                minLength={8}
                maxLength={72}
                disabled={busy}
              />
              <div style={{ color: 'var(--text-dim)', fontSize: 11, marginTop: -2 }}>
                At least 8 characters.
              </div>
              {err && <Banner tone="err">{err}</Banner>}
              <button
                type="submit"
                className="rl-btn-primary"
                disabled={busy}
                style={{ width: '100%', padding: '12px 16px', fontSize: 14, marginTop: 2 }}
              >
                {busy ? 'One moment…' : 'Set password'}
              </button>
            </form>
            <div style={{ marginTop: 14, fontSize: 12.5 }}>
              <a href="/login" style={{ color: 'var(--text-dim)', textDecoration: 'underline' }}>
                Back to log in
              </a>
            </div>
          </>
        )}
      </div>

      <Footer compact />
    </div>
  );
}
